<template>
  <v-container style="max-width: none !important;" >
    <RequestHeaderForm
        :request-id="currentRequest._id"
        :request-status="currentRequest.requestStatus"
        :request-date="currentRequest.requestDate"
        :approved-date="currentRequest.approvedDate"
        :requester="currentRequest.requester"
        :request-type="currentRequest.requestType"
        :approved-by="currentRequest.approvedBy"
        :name="currentRequest.name"
        :vendor="currentRequest.vendor"
        @data-changed="headerChanged"
        v-on:approve="approve"
        v-on:submit-for-pricing="submitForPricing"
    />
    <v-data-table
        :headers="headers"
        :items="currentRequest.requestLines"
        :expand="expand"
        item-key="requestLineId"
        class="elevate-4 overflow-fix"
        :loading="$store.state.loading > 0"
    >
      <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
      <template v-slot:items="props">
        <template v-if="editingRow !== props.item.requestLineId">
          <tr>
            <td class="justify-center layout px-0" nowrap>
              <v-spacer />

              <v-icon
                  small
                  class="mr-2"
                  @click="editingRow = props.item.requestLineId"
              >
                edit
              </v-icon>
              <v-spacer />

              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon
                      small
                      class="mr-2"
                      :color="props.item.note ? 'green' : 'rgba(0,0,0,0.54)'"
                      @click="noteClick(props.item)"
                       v-on="on"
                  >
                    {{ iconSelector(props.item) }}
                  </v-icon>
                </template>
                <span>{{ props.item.note }}</span>
              </v-tooltip>

              <v-spacer />

              <v-icon
                  small
                  @click="deleteRow(props.item.requestLineId)"
              >
                delete
              </v-icon>
              <v-spacer />
              <v-icon
                  small
                  @click="addSerial(props.item.requestLineId)"
              >
                fa-barcode
              </v-icon>
              <v-spacer />
              <v-icon
                  small
                  @click="props.expanded = !props.expanded"
              >
                keyboard_arrow_right
              </v-icon>
            </td>
            <td @dblclick="editingRow = props.item.requestLineId" nowrap>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <div v-on="on">{{ props.item.itemId }}</div>
                </template>
                <span>Minimum Version: {{ getMinVersion(props.item.itemId) }}</span>
              </v-tooltip>
            </td>
            <td class="text-xs-right narrow">{{ props.item.quantityRequested }}</td>
            <td class="text-xs-right narrow">{{ formatMoney(props.item.price) }}</td>
            <td class="text-xs-right narrow">{{ props.item.salesOrder }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityAwaitingApproval }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityApprovedToFulfill }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityInTesting }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityPassedTesting }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityFailedTesting }}</td>
            <td class="text-xs-right narrow-pad-right">{{ props.item.quantityRemainingToFulfill }}</td>
          </tr>
        </template>
        <template v-if="editingRow === props.item.requestLineId">
          <tr>
            <td class="justify-center layout px-0">
              <v-spacer />

              <v-icon
                  small
                  class="mr-2"
                  @click="saveRowChanges()"
              >
                save
              </v-icon>
              <v-spacer />
              <v-icon
                  small
                  @click="deleteRow()"
              >
                delete
              </v-icon>
              <v-spacer />
              <v-icon
                  small
                  @click="props.expanded = !props.expanded"
              >
                keyboard_arrow_right
              </v-icon>
            </td>
            <td class="remove-padding narrow-pad-right">
              <v-autocomplete
                  v-model="props.item.itemId"
                  :items="$store.state.partNumbers"
                  :loading="isLoading"
                  :search-input.sync="search"
                  label="Part Numbers"
                  item-text="itemId"
                  placeholder="Start typing to Search"
                  prepend-icon="mdi-database-search"
                  @change="changeItem"
                  read-only="false"
                  disable-initial-sort
              ></v-autocomplete>
            </td>
            <td class="narrow-pad-right" ><v-text-field v-model="props.item.quantityRequested" label="Qty Requested" required reverse ></v-text-field></td>
            <td class="narrow-pad-right" style='min-width: 120px;' >
              <v-text-field v-if="$store.state.user.isMdsiUser" v-model="props.item.price" label="Price" required reverse></v-text-field>
              <span v-else>{{ props.item.price }}</span>
            </td>
            <td class="narrow-pad-right" style='min-width: 120px;' >
              <v-text-field v-if="$store.state.user.isMdsiUser" v-model="props.item.salesOrder" label="Sales Order" required reverse></v-text-field>
              <span v-else>{{ props.item.salesOrder }}</span>
            </td>
            <td class="text-xs-right narrow">{{ props.item.quantityAwaitingApproval }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityApprovedToFulfill }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityInTesting }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityPassedTesting }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityFailedTesting }}</td>
            <td class="text-xs-right narrow">{{ props.item.quantityRemainingToFulfill }}</td>
          </tr>
        </template>
      </template>
      <template v-slot:expand="props">
        <v-card>
          <v-data-table
              :headers="serialHeaders"
              :items="props.item.serialLines"
              class="elevation-1 overflow-fix"
              item-key="serialNumber"
              hide-actions
          >
            <template v-slot:items="serialProps">
              <tr>
                <td class="justify-center layout px-0" nowrap>
                  <v-icon
                      small
                      class="mr-2"
                      @click="editSerial(serialProps, props.item.itemId)"
                  >
                    edit
                  </v-icon>
                  <v-icon
                      small
                      @click="deleteSerial(serialProps, props.item.serialNumber)"
                  >
                    delete
                  </v-icon>
                </td>
                <td nowrap>{{serialProps.item.serialNumber}}</td>
                <td nowrap>{{serialProps.item.dateAdded}}</td>
                <td nowrap>{{serialProps.item.ecoStatus}}</td>
                <td nowrap>{{serialProps.item.ecoDate}}</td>
                <td nowrap>{{serialProps.item.fulfillmentStatus}}</td>
               <!-- <td nowrap>{{serialProps.item.vendorId}}</td>
                <td nowrap>{{serialProps.item.sourcedBy}}</td>
                <td nowrap>{{serialProps.item.poNumber}}</td> -->
                <td nowrap>{{serialProps.item.shipDate}}</td>
                <td nowrap><a :href="getTracking(serialProps.item.tracking)" target="_blank">{{serialProps.item.tracking}}</a></td>
                <td nowrap>{{serialProps.item.testingStatus}}</td>
                <td nowrap>{{serialProps.item.rmaReasonCode}}</td>
                <td nowrap>{{serialProps.item.rmaNumber}}</td>
                <td nowrap>{{serialProps.item.rmaTracking}}</td>
                <td nowrap>{{serialProps.item.rmaReceived}}</td>
                <td nowrap>{{serialProps.item.invoiceStatus}}</td>
                <td nowrap>{{serialProps.item.invoiceNumber}}</td>

              </tr>
            </template>
          </v-data-table>

          <v-card-actions>
            <v-layout>
              <v-btn
                  flat
                  color="info"
                  @click="addSerial(props.item.requestLineId)"
              >
                Add Serial to {{ props.item.itemId }}
              </v-btn>
            </v-layout>

          </v-card-actions>
        </v-card>
      </template>
      <template v-slot:actions-prepend>

        <v-btn
            small
            color="info"
            @click="saveRowChanges()"
        >
          <v-icon>save</v-icon>&nbsp; Save</v-btn>

        <v-btn
            small
            @click="addLine"
        >
          <v-icon>add</v-icon> Add Line</v-btn>
      </template>
    </v-data-table>

    <v-expansion-panel class="top-spacing bottom-spacing" v-if="currentRequest.historyCount > 0" @change="historyPanelChange" @click="historyPanelClick">
      <v-expansion-panel-content>
        <template v-slot:header>
          <div>
            History
            <v-chip
                class="ma-2"
                color="green"
                text-color="white"
            >{{currentRequest.historyCount}}</v-chip>
          </div>
        </template>
        <v-card>
          <v-card-title>
            Nutrition
            <v-spacer></v-spacer>
            <v-text-field
                v-model="historySearch"
                append-icon="search"
                label="Search"
                single-line
                hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
              :headers="historyHeaders"
              :items="history"
              :search="historySearch"
              :rows-per-page-items="historyRowsPerPage"
          >
            <template v-slot:items="props">
              <td nowrap>{{ props.item.user.name }}</td>
              <td>{{ props.item.timestamp }}</td>
              <td>{{ props.item.updateSource }}</td>
              <td>
                <v-data-iterator
                    :items="props.item.differences"
                    content-tag="table"
                    row
                    hide-actions
                >
                  <template v-slot:item="innerProps">
                    <tr>
                      <td><v-chip :color="diffType[innerProps.item.kind].color">{{ diffType[innerProps.item.kind].text }}</v-chip></td>
                      <td><div>
                        {{ historyPath(innerProps.item.path) }}
                        <h4>{{ innerProps.item.lhs }} <span v-if="innerProps.item.lhs">-></span>
                          {{ innerProps.item.rhs }}</h4>
                      </div></td>
                    </tr>
                  </template>
                </v-data-iterator>
              </td>
            </template>
            <template v-slot:no-results>
              <v-alert :value="true" color="error" icon="warning">
                Your search for "{{ historySearch }}" found no results.
              </v-alert>
            </template>
          </v-data-table>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-dialog
      v-model="noteDialog"
      max-width="290"
    >
      <v-card>
        <v-card-title class="headline">Notes</v-card-title>

        <v-card-text>
          <v-textarea
            name="input-7-1"
            label="Enter note text"
            v-model="selectedNote"
            hint=""
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            color="green darken-1"
            flat="flat"
            @click="noteDialog = false"
          >
            Cancel
          </v-btn>

          <v-btn
            color="green darken-1"
            flat="flat"
            @click="saveNote(selectedNote)"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
        v-model="serialDialog"
        width="500"
    >
      <SerialNumberModal
      :request-id="currentRequest._id"
      :request-line-id="requestLineId"
      v-on:save="saveSerial"
      v-on:save-bulk="saveSerialBulk"
      :existing-serial="editingSerialItem"
      :request-item-id="editingRowItemId"
      allow-bulk="true"
      />
    </v-dialog>
  </v-container>
</template>

<script>
  import RequestHeaderForm from './RequestHeaderForm';
  import SerialNumberModal from './SerialNumberModal';
  import http from '../http';
  import { baseApiUrl } from '../config';
  import moment from 'moment';
  import formatters from '../formatting';

  export default {
    name: 'BuybackRequest',
    data() {
      return {
        expand: false,
        currentRequest: {},
        editingRow: -1,
        historyHeaders: [
          { text: 'Modified By', value: 'user.name' },
          { text: 'Change Date', value: 'timestamp' },
          { text: 'Update Source', value: 'updateSource' },
          { text: 'Changes', value: 'differenceValues' },
        ],
        headers: [
          { text: 'Actions', value: '', sortable: false },

          { text: 'Requested Item Id', value: 'itemId' },
          { text: 'Qty Requested', value: 'quantityRequested', align: 'right' , class: 'narrow'},
          { text: 'Price', value: 'price', align: 'right', class: 'narrow' },
          { text: 'Sales Order', value: 'salesOrder', align: 'right', class: 'narrow' },
          { text: 'Awaiting Approval', value: 'quantityAwaitingApproval', align: 'right', class: 'narrow', sortable: false },
          { text: 'Approved to Fulfill', value: 'quantityApprovedToFulfill', align: 'right', class: 'narrow', sortable: false },
          { text: 'In Testing', value: 'quantityPassedTesting', align: 'right', class: 'narrow', sortable: false },
          { text: 'Passed Testing', value: 'quantityPassedTesting', align: 'right', class: 'narrow', sortable: false },
          { text: 'Failed Testing', value: 'quantityFailedTesting', align: 'right', class: 'narrow', sortable: false },
          { text: 'Remaining to Fulfill', value: 'quantityRemainingToFulfill', align: 'right', class: 'narrow-pad-right', sortable: false },
        ],
        serialHeaders: [
          { text: '', value: '', sortable: false, align: 'center', class: 'narrow' },
          { text: 'Serial #', value: 'serialNumber', align: 'left', class: 'narrow' },
          { text: 'Date Added', value: 'dateAdded', align: 'left', class: 'narrow' },
          { text: 'ECO Status', value: 'ECOStatus', class: 'narrow' },
          { text: 'ECO Date', value: 'ECODate', class: 'narrow' },
          { text: 'Fulfillment Status', value: 'fulfillmentStatus', class: 'narrow' },
          /* { text: 'Vendor', value: 'vendorId' },
          { text: 'Sourced By', value: 'sourcedBy' },
          { text: 'PO Number', value: 'poNumber' }, */
          { text: 'Ship Date', value: 'shipDate', class: 'narrow' },

          { text: 'Tracking', value: 'tracking', class: 'narrow' },
          { text: 'Testing Status', value: 'TestingStatus', class: 'narrow' },
          { text: 'RMA Reason', value: 'rmaReasonCode', class: 'narrow' },

          { text: 'RMA Number', value: 'RMANumber', class: 'narrow' },
          { text: 'RMA Tracking', value: 'rmaTracking', class: 'narrow' },
          { text: 'RMA Received', value: 'rmaReceived', class: 'narrow' },

          { text: 'Inv. Status', value: 'invoiceStatus', class: 'narrow' },
          { text: 'Inv. Number', value: 'invoiceNumber', class: 'narrow' },

        ],

        selectedNote: null,
        isLoading: false,
        count: 0,
        search: null,
        results: [],
        item: null,
        serialDialog: false,
        noteDialog: false,
        requestLineId: 0,
        editingSerialItem: null,
        editingRowItemId: '',
        history: undefined,
        historyRowsPerPage: [25, 50, 100,{"text":"$vuetify.dataIterator.rowsPerPageAll","value":-1}],
        historySearch: '',
        diffType: {
          "N": { text: "New", color: "yellow" },
          "A": { text: "Add", color: "green"},
          "E": { text: "Edit", color: "blue"},
          "D": { text: "Delete", color: "red"},
        },
      };
    },
    props: ['id'],
    computed: {
    },
    components: {
      RequestHeaderForm,
      SerialNumberModal,
    },
    async mounted() {
      this.$store.commit('SET_BREADCRUMBS', [
        { text: 'BuyBack Requests', disabled: false, href: '/#/buyback-requests'},
        { text: this.id, disabled: true },
      ]);

      await this.loadRequest();
    },
    watch: {
      item(value) {
        console.log('watch item', value);
      },
      search (value) {
        console.log('search::value', value);
        // Items have already been loaded
        if (this.results && this.results.length > 0) return;

        // Items have already been requested
        if (this.isLoading) return;

        this.isLoading = true;

        this.results = this.$store.state.partNumbers;

        this.isLoading = false;
      }
    },
    methods: {
      historyPath(pathArray) {
        const path = pathArray.map((elem) => {
          if (Number.isNaN(Number(elem))) {
            return elem;
          } else {
            return `[${elem}]`;
          }
        });

        return path.join('.');
      },
      historyPanelChange(a, b) {
        console.log('historyPanelChange', a, b);
      },

      historyPanelClick(a, b) {
        console.log('historyPanelClick', a, b);
      },

      getMinVersion(itemId) {
        try {
          const part = this.$store.state.partNumbers.find(p => p.itemId === itemId);
          if (part) {
            return part.minimumVersion;
          }
        } catch (err) {
          console.log('error finding minVersion: ', err.message);
          return;
        }

        return 'no minimum version specified.';
      },
      iconSelector(item) {
        if (!item.note) {
          return 'fa-comment-alt-plus';
        }

        return 'fa-comment-alt-dots';
      },
      noteClick(item) {
        this.noteDialog = true;
        this.selectedNote = item.note;
        this.item = item;
      },
      getTracking(trackingNumber) {
        if (!trackingNumber) return '';
        if (!trackingNumber.indexOf('4') === 0) return '';

        return `https://www.fedex.com/apps/fedextrack/?tracknumbers=${trackingNumber}`;
      },
      formatMoney(v) {
        return formatters.money(v);
      },
      async loadRequest() {
        const [ dataResp, historyResp ] = await Promise.all(
          [
            await http.get(`${baseApiUrl}/request/${this.id}`),
            await http.get(`${baseApiUrl}/history/request/${this.id}`)
            ]
        );

        this.currentRequest = dataResp.data;
        console.log('loadRequest, currentRequest:', dataResp.data);

        if (historyResp && historyResp.data) {
          this.history = historyResp.data.map(row => {
            let differenceValues = [];
            row.differences.forEach(diff => {
              console.log('diff: ', diff);
              if (diff.rhs) {
                differenceValues.push(diff.rhs);
              } else if (diff.item) {
                if (typeof diff.item.rhs === 'object') {
                  differenceValues = differenceValues.concat(Object.keys(diff.item.rhs).map(k => diff.item.rhs[k]));
                } else {
                  differenceValues.push(diff.rhs);
                }
              }
            });

            return {...row, differenceValues: differenceValues.join(',')};
          });

          console.log('history:', this.history);
        }
      },
      addLine() {
        let max = 0;
        for(let line of this.currentRequest.requestLines) {
          if (line.requestLineId > max) {
            max = line.requestLineId;
          }
        }
        max++;
        const newLine = {
          requestLineId: max
        };

        this.currentRequest.requestLines.push(newLine);
        this.editingRow = max;
      },
      async saveRowChanges() {
        console.log('saveRowChanges:', this.currentRequest);
        this.editingRow = -1;
        await http.put(`${baseApiUrl}/request/${this.currentRequest._id}`, this.currentRequest);
      },
      async approve() {
        this.currentRequest.approvedDate = moment();
        this.currentRequest.approvedBy = this.$store.state.user.attributes.name;
        this.currentRequest.requestStatus = 'Approved for Fulfillment';
        await this.saveRowChanges();
      },
      async submitForPricing() {
        this.currentRequest.requestStatus = 'Submitted for Pricing';
        await this.saveRowChanges();
      },
      changeItem(itemId) {
        const arrayIndex = this.currentRequest.requestLines.findIndex((rl) => rl.requestLineId === this.editingRow);
        console.log('changeItem:', itemId);
        console.log('arrayIndex:', arrayIndex);
        console.log('this.editingRow:',this.editingRow);
        console.log('this.currentRequest.requestLines[arrayIndex]:', this.currentRequest.requestLines[arrayIndex]);
        const partIndex = this.$store.state.partNumbers.findIndex((i) => i.itemId === itemId);
        if (partIndex >= 0) {
          this.currentRequest.requestLines[arrayIndex].price = Number.parseFloat(this.$store.state.partNumbers[partIndex].ciscoRefreshListPrice);
        }

        this.currentRequest.requestLines[arrayIndex].requestItemId = itemId;
      },
      headerChanged(changes) {
        console.log('headerChanged:', changes);
        this.currentRequest = { ...this.currentRequest, ...changes };
      },
      async deleteRow(requestLineId) {
        console.log('deleteRow - requestLineId:', requestLineId);

        if (requestLineId < 0) return;

        let rowToDelete = this.currentRequest.requestLines.findIndex(rl => rl.requestLineId === requestLineId);
        console.log('rowToDelete: ', rowToDelete);
        console.log('this.editingRow: ', this.editingRow);

        //if (this.editingRow < 0) rowToDelete = index;

        this.currentRequest.requestLines.splice(rowToDelete, 1);
        await this.saveRowChanges();
      },
      addSerial(requestLineId) {
        console.log('addSerial - requestLineId:', requestLineId);

        if (requestLineId < 0) return;

        let rowIndex = this.currentRequest.requestLines.findIndex(rl => rl.requestLineId === requestLineId);

        if (rowIndex < 0) return;

        console.log('add serial for:', rowIndex, this.currentRequest);
        this.requestLineId = this.currentRequest.requestLines[rowIndex].requestLineId;
        console.log('add serial for:', rowIndex, 'this.requestLineId:', this.requestLineId);
        this.editingSerialItem = null;
        this.editingRowItemId = this.currentRequest.requestLines[rowIndex].itemId;
        this.serialDialog = true;
      },
      async saveNote(note) {
        console.log('save note:', this.item, this.selectedNote, note);
        await http.put(`${baseApiUrl}/request/note/${this.currentRequest._id}/${this.item.requestLineId}`, this.selectedNote);
        this.noteDialog = false;
        this.selectedNote = null;
        await this.loadRequest();
      },
      async saveSerial(updatedSerial) {
        console.log('updatedSerial Data:', updatedSerial, updatedSerial.requestLineId);
        await http.put(`${baseApiUrl}/request/serial/${this.currentRequest._id}/${updatedSerial.requestLineId}`, updatedSerial);
        this.serialDialog = false;
        await this.loadRequest();
      },
      async saveSerialBulk(bulkSerial) {
        console.log('saveSerialBulk:', bulkSerial);
        const trimmedArray = [];
        for(let serial of bulkSerial.serials) {
          const trimmed = serial.trim();
          if (trimmed && trimmed.length > 0) {
            trimmedArray.push(trimmed);
          }
        }

        if (trimmedArray.length === 0) {
          this.serialDialog = false;
          return;
        }

        await http.put(`${baseApiUrl}/request/serial/${bulkSerial.requestId}/${bulkSerial.requestLineId}`, trimmedArray);
        this.serialDialog = false;
        await this.loadRequest();
      },
      editSerial(serialProps, itemId) {
        console.log('editSerial:', serialProps, itemId);
        this.editingRowItemId = itemId;
        this.editingSerialItem = serialProps.item;
        this.serialDialog = true;
      },
      async deleteSerial(props) {
        const { requestId, requestLineId, serialNumber } = props.item;
        console.log('serialNumber:', serialNumber); // /request/serial/{id}/{requestLineId}/{serialNumber}
        console.log('requestId:', requestId);
        console.log('requestLineId:', requestLineId);

        await http.delete(`${baseApiUrl}/request/serial/${requestId}/${requestLineId}/${serialNumber}`);
        this.serialDialog = false;
        await this.loadRequest();
      },
    },
    async beforeCreate() {

    },

    beforeUpdate() {
    },
  };
</script>

<style>
  .remove-padding {
    padding: 0px !important;
    min-width: 250px;
  }

  .narrow {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .narrow-pad-right {
    padding-top: 0px !important;
    padding-bottom: 0px !important;
    padding-left: 0px !important;
    padding-right: 8px !important;
  }

  .overflow-fix {
    overflow-x: auto !important;
  }

  .top-spacing {
    margin-top: 15px;
  }

  .bottom-spacing {
    margin-bottom: 15px;
  }
</style>
